import { getCurrentServer, getOpenAccessTokenKey, getPrefixUrl } from '@/utils/globalData'
import { getRedirectSubDomain, normalizeTrackingHost } from '@/utils/eventTracking'
import { useMainStore } from '@/composables/store'

export const ONLINE_VISIT_ENDPOINT = '/mall/admin/online/recordVisit?isShop=1&uncheck401=1'

export const ONLINE_VISIT_PAGE_TYPES = Object.freeze({
  SHOP_HOME: 'SHOP_HOME',
  PRODUCT_DETAIL: 'PRODUCT_DETAIL',
  CHECKOUT: 'CHECKOUT',
  ADD_CART: 'ADD_CART',
  OTHER: 'OTHER',
})

const VISITOR_ID_STORAGE_KEY = 'mall_online_visitor_id'
const VISITOR_ID_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60
const ADD_CART_REPORT_DEDUP_MS = 1000

let lastAddCartReportAt = 0
let addCartReportPromise = null
let cachedOnlineVisitCustomFetch = null

const normalizeValue = (value) => String(value ?? '').trim()

const safeParseJson = (value) => {
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const readCookieValue = (name) => {
  if (!process.client || typeof document === 'undefined') return ''

  try {
    const encodedName = encodeURIComponent(name)
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodedName}=([^;]*)`))
    return match?.[1] ? decodeURIComponent(match[1]) : ''
  } catch (error) {
    return ''
  }
}

const writeCookieValue = (name, value) => {
  if (!process.client || typeof document === 'undefined') return

  try {
    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      'Path=/',
      `Max-Age=${VISITOR_ID_COOKIE_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ]

    if (window.location.protocol === 'https:') {
      parts.push('Secure')
    }

    document.cookie = parts.join('; ')
  } catch (error) {
    // Ignore cookie persistence errors; reporting must never block the page.
  }
}

const readLocalStorageValue = (key) => {
  if (!process.client || typeof localStorage === 'undefined') return ''

  try {
    return normalizeValue(localStorage.getItem(key))
  } catch (error) {
    return ''
  }
}

const writeLocalStorageValue = (key, value) => {
  if (!process.client || typeof localStorage === 'undefined' || !value) return

  try {
    localStorage.setItem(key, value)
  } catch (error) {
    // Ignore storage errors; cookie fallback still gives us a visitor id.
  }
}

const getMainStoreSafely = () => {
  try {
    return useMainStore()
  } catch (error) {
    return null
  }
}

const getCurrentUrl = () => {
  if (!process.client || typeof window === 'undefined') return ''
  return `${window.location.pathname || '/'}${window.location.search || ''}`
}

export const getOnlineVisitorId = () => {
  if (!process.client) return ''

  const storedVisitorId =
    readLocalStorageValue(VISITOR_ID_STORAGE_KEY) ||
    normalizeValue(readCookieValue(VISITOR_ID_STORAGE_KEY))

  if (storedVisitorId) {
    return storedVisitorId
  }

  const randomPart = Math.random().toString(36).slice(2, 10)
  const visitorId = `web_${randomPart}_${Date.now()}`
  writeLocalStorageValue(VISITOR_ID_STORAGE_KEY, visitorId)
  writeCookieValue(VISITOR_ID_STORAGE_KEY, visitorId)

  return visitorId
}

export const getOnlineVisitDomain = () => {
  if (!process.client || typeof window === 'undefined') return ''

  return normalizeTrackingHost(
    getRedirectSubDomain() ||
    window.location.host ||
    ''
  )
}

export const resolveOnlineVisitShopId = (options = {}) => {
  if (!process.client) return ''

  const store = getMainStoreSafely()
  const currentServer = getCurrentServer()
  const tokenKey = getOpenAccessTokenKey(currentServer)
  const candidates = [
    options.shopId,
    options.dealerShopId,
    typeof store?.getShopId === 'function' ? store.getShopId(currentServer) : '',
    typeof store?.getDealerShopId === 'function' ? store.getDealerShopId(currentServer) : '',
    typeof store?.getServerShopId === 'function' ? store.getServerShopId(currentServer) : '',
    store?.$state?.shopIdKeyMap?.[tokenKey],
    store?.$state?.dealerShopIdKeyMap?.[tokenKey],
    store?.$state?.siteInfo?.configData?.shopId,
    store?.$state?.siteInfo?.configData?.dealerShopId,
    store?.$state?.siteInfo?.shopId,
    store?.$state?.siteInfo?.dealerShopId,
    readCookieValue('shopId'),
    readCookieValue('dealerShopId'),
    readLocalStorageValue('shopId'),
    readLocalStorageValue('dealerShopId'),
  ]

  return normalizeValue(candidates.find((item) => normalizeValue(item)))
}

const resolveOnlineVisitUserId = () => {
  if (!process.client) return ''

  const store = getMainStoreSafely()
  const localUserInfo =
    safeParseJson(readLocalStorageValue('userInfo')) ||
    safeParseJson(readLocalStorageValue('mallUserInfo')) ||
    {}

  const candidates = [
    store?.mallUserInfo?.id,
    store?.mallUserInfo?.userId,
    store?.mallUserInfo?.customerInfoId,
    store?.userInfo?.id,
    store?.userInfo?.userId,
    store?.userInfo?.customerInfoId,
    localUserInfo?.id,
    localUserInfo?.userId,
    localUserInfo?.customerInfoId,
  ]

  return normalizeValue(candidates.find((item) => normalizeValue(item)))
}

export const resolveOnlineVisitPageType = (url = '') => {
  const rawUrl = normalizeValue(url) || getCurrentUrl()
  const path = (rawUrl.split('?')[0] || '/').toLowerCase()
  const query = rawUrl.includes('?') ? rawUrl.slice(rawUrl.indexOf('?') + 1) : ''
  const searchParams = new URLSearchParams(query)

  if (path === '/' || path === '/index') {
    return ONLINE_VISIT_PAGE_TYPES.SHOP_HOME
  }

  if (path.startsWith('/payment') || path.startsWith('/checkout')) {
    return ONLINE_VISIT_PAGE_TYPES.CHECKOUT
  }

  if (path === '/product' && searchParams.has('detailId')) {
    return ONLINE_VISIT_PAGE_TYPES.PRODUCT_DETAIL
  }

  const productListPaths = [
    '/product/categorylist',
    '/product/goodslist',
    '/product/shopsearch',
    '/product/search',
  ]
  const isProductListPath = productListPaths.some((listPath) => (
    path === listPath || path.startsWith(`${listPath}/`)
  ))

  if (
    !isProductListPath && (
      path.startsWith('/product/detail') ||
      path.startsWith('/product/') ||
      path.startsWith('/products/')
    )
  ) {
    return ONLINE_VISIT_PAGE_TYPES.PRODUCT_DETAIL
  }

  return ONLINE_VISIT_PAGE_TYPES.OTHER
}

export const buildOnlineVisitPayload = (options = {}) => {
  const url = normalizeValue(options.url) || getCurrentUrl()
  const pageType = normalizeValue(options.pageType) || resolveOnlineVisitPageType(url)
  const userId = resolveOnlineVisitUserId()
  const param = {
    domain: getOnlineVisitDomain(),
    visitorId: getOnlineVisitorId(),
    pageType,
    url,
  }

  if (userId) {
    param.userId = userId
  }

  return { param }
}

const buildOnlineVisitHeaders = (options = {}) => {
  const shopId = resolveOnlineVisitShopId(options)

  if (!shopId) {
    return {}
  }

  return {
    shopId,
  }
}

const getOnlineVisitCustomFetch = (options = {}) => {
  if (typeof options.nuxtApp?.$customFetch === 'function') {
    cachedOnlineVisitCustomFetch = (request, fetchOptions) =>
      options.nuxtApp.$customFetch(request, fetchOptions)
    return cachedOnlineVisitCustomFetch
  }

  if (cachedOnlineVisitCustomFetch) {
    return cachedOnlineVisitCustomFetch
  }

  try {
    const nuxtApp = useNuxtApp()
    if (typeof nuxtApp?.$customFetch === 'function') {
      cachedOnlineVisitCustomFetch = (request, fetchOptions) =>
        nuxtApp.$customFetch(request, fetchOptions)
      return cachedOnlineVisitCustomFetch
    }
  } catch (error) {
    // Add-to-cart callbacks may run after Nuxt's active context has ended.
  }

  return null
}

const sendOnlineVisitWithNativeFetch = async (payload, headers, options = {}) => {
  if (typeof fetch !== 'function') return null

  const response = await fetch(`${getPrefixUrl()}${ONLINE_VISIT_ENDPOINT}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(payload),
    keepalive: options.keepalive === true,
  })

  if (!response.ok) return null

  const responseText = await response.text()
  if (!responseText) return { success: true }

  try {
    return JSON.parse(responseText)
  } catch (error) {
    return { success: true }
  }
}

export const sendOnlineVisit = async (pageType, options = {}) => {
  if (!process.client) return null

  try {
    const payload = buildOnlineVisitPayload({
      ...options,
      pageType,
    })
    const shopId = resolveOnlineVisitShopId(options)
    const customFetch = getOnlineVisitCustomFetch(options)

    // $customFetch can inject the current shop id after an async UI callback.
    if (!payload.param.domain || !payload.param.visitorId || (!shopId && !customFetch)) {
      return null
    }

    const headers = buildOnlineVisitHeaders({ ...options, shopId })

    if (customFetch) {
      try {
        return await customFetch(ONLINE_VISIT_ENDPOINT, {
          method: 'POST',
          headers,
          body: payload,
        })
      } catch (error) {
        // Async click callbacks can lose Nuxt request context; retry with native fetch.
      }
    }

    if (!shopId) {
      return null
    }

    return await sendOnlineVisitWithNativeFetch(payload, headers, options)
  } catch (error) {
    return null
  }
}

export const reportAddCartVisit = (options = {}) => {
  if (!process.client) return null

  const shopId = resolveOnlineVisitShopId(options)

  const now = Date.now()
  if (now - lastAddCartReportAt < ADD_CART_REPORT_DEDUP_MS) {
    return null
  }

  if (addCartReportPromise) {
    return addCartReportPromise
  }

  addCartReportPromise = Promise.resolve(
    sendOnlineVisit(ONLINE_VISIT_PAGE_TYPES.ADD_CART, {
      ...options,
      shopId,
    }),
  )
    .then((result) => {
      if (result && result.success !== false) {
        lastAddCartReportAt = Date.now()
      }
      return result
    })
    .finally(() => {
      addCartReportPromise = null
    })

  return addCartReportPromise
}

export const sendOnlineVisitKeepalive = (pageType, options = {}) => {
  if (!process.client || typeof fetch !== 'function') return

  try {
    const payload = buildOnlineVisitPayload({
      ...options,
      pageType,
    })
    const shopId = resolveOnlineVisitShopId(options)

    if (!payload.param.domain || !payload.param.visitorId || !shopId) {
      return
    }

    fetch(`${getPrefixUrl()}${ONLINE_VISIT_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildOnlineVisitHeaders({ ...options, shopId }),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {})
  } catch (error) {
    // no-op
  }
}
